import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'dark' | 'ghost' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-medium transition-all duration-200 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantStyles = {
      primary: 'bg-lime-400 text-dark-800 hover:bg-lime-500 focus:ring-lime-400 shadow-sm hover:shadow-md',
      secondary: 'bg-lavender-400 text-dark-800 hover:bg-lavender-500 focus:ring-lavender-400 shadow-sm hover:shadow-md',
      dark: 'bg-dark-800 text-white hover:bg-dark-700 focus:ring-dark-600 dark:bg-dark-600 dark:hover:bg-dark-500',
      ghost: 'bg-transparent text-dark-800 hover:bg-gray-100 focus:ring-gray-300 dark:text-gray-100 dark:hover:bg-dark-700',
      outline: 'bg-transparent border-2 border-dark-800 text-dark-800 hover:bg-dark-800 hover:text-white focus:ring-dark-600 dark:border-gray-400 dark:text-gray-100 dark:hover:bg-dark-600',
      danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-400 shadow-sm hover:shadow-md',
    };

    const sizeStyles = {
      sm: 'px-4 py-2 text-sm gap-1.5',
      md: 'px-6 py-3 text-base gap-2',
      lg: 'px-8 py-4 text-lg gap-2.5',
    };

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
